import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Save, Trash2, Sparkles, Upload, Copy, Instagram, AtSign, CalendarDays, List as ListIcon, ChevronLeft, ChevronRight, Wand2, Images, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const PLATFORMS = [
  { id: "instagram", label: "Instagram", Icon: Instagram },
  { id: "threads", label: "Threads", Icon: AtSign },
];
const AUDIENCES = [
  { id: "members", label: "Members / Patients", color: "#C05C42" },
  { id: "clinicians", label: "Clinicians", color: "#3F6B4C" },
];
const STATUSES = ["idea", "approved", "scheduled", "posted"];
const IMG_STYLES = [
  { id: "botanical", label: "Botanical" },
  { id: "photographic", label: "Photographic" },
  { id: "painterly", label: "Painterly" },
];

const Field = ({ label, value, onChange, multiline, rows = 3, testid, placeholder }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">{label}</label>
    {multiline ? (
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border border-[#D1C7B8] focus:border-[#C05C42] rounded-xl p-3 outline-none text-[#2D322B] text-sm" />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] text-sm" />
    )}
  </div>
);

function StrategyEditor() {
  const [s, setS] = useState({ overview: "", cadence: "", pillars: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/social/strategy").then((r) => setS({ overview: r.data.overview || "", cadence: r.data.cadence || "", pillars: r.data.pillars || [] })).catch(() => {});
  }, []);

  const setPillar = (i, k, v) => setS((x) => ({ ...x, pillars: x.pillars.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));
  const addPillar = () => setS((x) => ({ ...x, pillars: [...x.pillars, { title: "", description: "", audience: "members" }] }));
  const removePillar = (i) => setS((x) => ({ ...x, pillars: x.pillars.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/social/strategy", s);
      toast.success("Strategy saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const addFeaturePillars = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/social/strategy/add-feature-pillars");
      const st = r.data?.strategy || {};
      setS({ overview: st.overview || "", cadence: st.cadence || "", pillars: st.pillars || [] });
      const n = r.data?.added ?? 0;
      toast.success(n > 0 ? `Added ${n} feature-showcase pillar${n === 1 ? "" : "s"}.` : "All feature pillars are already in your strategy.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add feature pillars");
    } finally {
      setSaving(false);
    }
  };

  const addWomensPillars = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/social/strategy/add-womens-pillars");
      const st = r.data?.strategy || {};
      setS({ overview: st.overview || "", cadence: st.cadence || "", pillars: st.pillars || [] });
      const n = r.data?.added ?? 0;
      toast.success(n > 0 ? `Added ${n} Women's Vitality pillar${n === 1 ? "" : "s"}.` : "All Women's Vitality pillars are already in your strategy.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add Women's Vitality pillars");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6 space-y-4" data-testid="social-strategy-editor">
      <p className="label-eyebrow">Strategy</p>
      <Field label="Overview" value={s.overview} onChange={(v) => setS({ ...s, overview: v })} testid="strategy-overview" multiline rows={4} />
      <Field label="Cadence & platform mix" value={s.cadence} onChange={(v) => setS({ ...s, cadence: v })} testid="strategy-cadence" multiline rows={3} />
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-widest text-[#5C6359]">Content pillars</label>
          <button onClick={addPillar} className="btn-secondary !py-1 !px-3 !text-xs inline-flex items-center gap-1.5" data-testid="add-pillar"><Plus className="w-3 h-3" /> Pillar</button>
        </div>
        <div className="space-y-3">
          {s.pillars.map((p, i) => (
            <div key={i} className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-xl p-3 space-y-2" data-testid={`pillar-${i}`}>
              <div className="flex items-center gap-2">
                <input value={p.title || ""} onChange={(e) => setPillar(i, "title", e.target.value)} placeholder="Pillar title" data-testid={`pillar-title-${i}`}
                  className="flex-1 bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-1.5 outline-none text-[#2D322B] text-sm" />
                <select value={p.audience || "members"} onChange={(e) => setPillar(i, "audience", e.target.value)} data-testid={`pillar-audience-${i}`}
                  className="bg-transparent border border-[#D1C7B8] rounded-lg px-2 py-1.5 text-xs text-[#2D322B]">
                  {AUDIENCES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                <button onClick={() => removePillar(i)} className="text-[#8F3A3A] hover:text-[#C05C42]" data-testid={`remove-pillar-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <input value={p.description || ""} onChange={(e) => setPillar(i, "description", e.target.value)} placeholder="Description" data-testid={`pillar-desc-${i}`}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-1.5 outline-none text-[#5C6359] text-sm" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 inline-flex items-center gap-2" data-testid="strategy-save">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save strategy"}
        </button>
        <button onClick={addFeaturePillars} disabled={saving} className="btn-secondary !py-2 !px-4 inline-flex items-center gap-2" data-testid="strategy-add-feature-pillars" title="Add one content pillar per major Badi feature">
          <Plus className="w-4 h-4" /> Add feature pillars
        </button>
        <button onClick={addWomensPillars} disabled={saving} className="btn-secondary !py-2 !px-4 inline-flex items-center gap-2" data-testid="strategy-add-womens-pillars" title="Add Women's Vitality pillars (Cycles & Seasons)">
          <Plus className="w-4 h-4" /> Add Women's Vitality pillars
        </button>
      </div>
    </section>
  );
}

function PostEditor({ post, onSaved, onDeleted }) {
  const [p, setP] = useState(post);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [topic, setTopic] = useState("");
  const [slideCount, setSlideCount] = useState(4);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));

  const draft = async () => {
    setBusy("draft");
    try {
      const r = await api.post("/admin/social/draft", { platform: p.platform, audience: p.audience, pillar: p.pillar || null, topic: topic || null });
      setP((x) => ({ ...x, caption: r.data.caption, hashtags: r.data.hashtags }));
      toast.success("Massey drafted a post — refine it, then Save.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not draft");
    } finally { setBusy(null); }
  };

  const genImage = async (style) => {
    setBusy(`gen:${style}`);
    try {
      const title = topic || p.pillar || (p.caption || "").split("\n")[0] || "Badi social";
      const r = await api.post("/admin/library/generate-image", { title, category: `Badi social — ${p.audience}`, style });
      set("image", r.data.url);
      toast.success("Image generated — Save to keep it.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate image");
    } finally { setBusy(null); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/social/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", r.data.url);
      toast.success("Image uploaded — Save to keep it.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload");
    } finally { setBusy(null); e.target.value = ""; }
  };

  const copyPost = () => {
    const text = [p.caption, p.hashtags].filter(Boolean).join("\n\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Caption + hashtags copied."));
  };

  const buildCarousel = async () => {
    setBusy("carousel");
    try {
      const r = await api.post(`/admin/social/posts/${p.id}/carousel`, { count: slideCount });
      setP(r.data);
      onSaved(r.data);
      toast.success(`Carousel built — ${r.data.slides?.length || 0} slides. Add the copy over each image in Canva/Instagram.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build carousel");
    } finally { setBusy(null); }
  };
  const setSlideText = (i, v) => setP((x) => ({ ...x, slides: (x.slides || []).map((s, idx) => (idx === i ? { ...s, text: v } : s)) }));
  const removeSlide = (i) => setP((x) => ({ ...x, slides: (x.slides || []).filter((_, idx) => idx !== i) }));
  const copySlide = (t) => navigator.clipboard.writeText(t || "").then(() => toast.success("Slide copy copied."));

  const regenSlide = async (i) => {
    setBusy(`slide:${i}`);
    try {
      const r = await api.post(`/admin/social/posts/${p.id}/slides/${i}/regenerate`);
      setP(r.data);
      onSaved(r.data);
      toast.success(`Slide ${i + 1} image regenerated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate slide");
    } finally { setBusy(null); }
  };

  const downloadCarousel = async () => {
    setBusy("zip");
    try {
      const r = await api.get(`/admin/social/posts/${p.id}/carousel.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.href = url;
      a.download = m?.[1] || `badi-carousel-${p.id}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Carousel downloaded (images + captions.txt).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download carousel");
    } finally { setBusy(null); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/social/posts/${p.id}`, {
        caption: p.caption, platform: p.platform, audience: p.audience, pillar: p.pillar || null,
        hashtags: p.hashtags || null, image: p.image || null, slides: p.slides || null,
        status: p.status, target_date: p.target_date || null,
      });
      onSaved(r.data);
      toast.success("Post saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/social/posts/${p.id}`); onDeleted(p.id); }
    catch (e) { toast.error("Could not delete"); }
  };

  const aud = AUDIENCES.find((a) => a.id === p.audience) || AUDIENCES[0];

  return (
    <div className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6" data-testid={`social-post-${p.id}`}>
      <div className="grid md:grid-cols-[180px_1fr] gap-6">
        <div className="space-y-3">
          <div className="aspect-square bg-[#F9F6F0] border border-[#D1C7B8] rounded-xl overflow-hidden flex items-center justify-center">
            {p.image ? <img src={imgSrc(p.image)} alt="post" data-testid={`social-preview-${p.id}`} className="w-full h-full object-cover" />
              : <span className="text-xs text-[#9A8F7E] px-3 text-center">No image</span>}
          </div>
          <label className="btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5 cursor-pointer w-full justify-center" data-testid={`social-upload-${p.id}`}>
            <Upload className="w-3 h-3" /> {busy === "upload" ? "Uploading…" : "Upload"}
            <input type="file" accept="image/*" onChange={uploadImage} className="hidden" />
          </label>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-widest text-[#5C6359] w-full">Or generate:</span>
            {IMG_STYLES.map((st) => (
              <button key={st.id} type="button" onClick={() => genImage(st.id)} disabled={!!busy} data-testid={`social-gen-${p.id}-${st.id}`}
                className="btn-secondary !py-1 !px-2 !text-[11px] inline-flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> {busy === `gen:${st.id}` ? "…" : st.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {/* platform */}
            <div className="inline-flex rounded-lg border border-[#D1C7B8] overflow-hidden" data-testid={`social-platform-${p.id}`}>
              {PLATFORMS.map((pl) => (
                <button key={pl.id} onClick={() => set("platform", pl.id)} data-testid={`social-platform-${p.id}-${pl.id}`}
                  className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${p.platform === pl.id ? "bg-[#2D322B] text-[#F9F6F0]" : "text-[#5C6359]"}`}>
                  <pl.Icon className="w-3 h-3" /> {pl.label}
                </button>
              ))}
            </div>
            {/* audience */}
            <div className="inline-flex rounded-lg border border-[#D1C7B8] overflow-hidden" data-testid={`social-audience-${p.id}`}>
              {AUDIENCES.map((a) => (
                <button key={a.id} onClick={() => set("audience", a.id)} data-testid={`social-audience-${p.id}-${a.id}`}
                  className="px-3 py-1.5 text-xs" style={p.audience === a.id ? { background: a.color, color: "#fff" } : { color: "#5C6359" }}>
                  {a.label}
                </button>
              ))}
            </div>
            {/* status */}
            <select value={p.status} onChange={(e) => set("status", e.target.value)} data-testid={`social-status-${p.id}`}
              className="bg-transparent border border-[#D1C7B8] rounded-lg px-2 py-1.5 text-xs text-[#2D322B] capitalize">
              {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Pillar" value={p.pillar} onChange={(v) => set("pillar", v)} testid={`social-pillar-${p.id}`} placeholder="e.g. Bushfood as Medicine" />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#5C6359] block mb-1">Target date</label>
              <input type="date" value={p.target_date || ""} onChange={(e) => set("target_date", e.target.value)} data-testid={`social-date-${p.id}`}
                className="w-full bg-transparent border-b border-[#D1C7B8] focus:border-[#C05C42] py-2 outline-none text-[#2D322B] text-sm" />
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label="Topic for Massey (optional)" value={topic} onChange={setTopic} testid={`social-topic-${p.id}`} placeholder="e.g. Kakadu plum vitamin C" />
            </div>
            <button onClick={draft} disabled={!!busy} data-testid={`social-draft-${p.id}`} className="btn-secondary !py-2 !px-3 !text-xs inline-flex items-center gap-1.5 shrink-0">
              <Sparkles className="w-3 h-3" /> {busy === "draft" ? "Writing…" : "Draft with Massey"}
            </button>
          </div>

          <Field label="Caption" value={p.caption} onChange={(v) => set("caption", v)} testid={`social-caption-${p.id}`} multiline rows={5} />
          <Field label="Hashtags" value={p.hashtags} onChange={(v) => set("hashtags", v)} testid={`social-hashtags-${p.id}`} placeholder="#badi #longevity" />

          {/* Carousel */}
          <div className="border-t border-[#D1C7B8] pt-3">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="text-[10px] uppercase tracking-widest text-[#5C6359] inline-flex items-center gap-1.5">
                <Images className="w-3 h-3" /> Carousel — image + slide copy
              </label>
              <div className="flex items-center gap-1.5">
                <select value={slideCount} onChange={(e) => setSlideCount(Number(e.target.value))} data-testid={`social-carousel-count-${p.id}`}
                  className="bg-transparent border border-[#D1C7B8] rounded-lg px-2 py-1 text-xs text-[#2D322B]">
                  {[3, 4, 5].map((n) => <option key={n} value={n}>{n} slides</option>)}
                </select>
                <button type="button" onClick={buildCarousel} disabled={!!busy} data-testid={`social-build-carousel-${p.id}`}
                  className="btn-secondary !py-1 !px-2.5 !text-[11px] inline-flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" /> {busy === "carousel" ? "Building…" : (p.slides?.length ? "Rebuild" : "Build carousel")}
                </button>
                {p.slides?.length ? (
                  <button type="button" onClick={downloadCarousel} disabled={!!busy} data-testid={`social-download-carousel-${p.id}`}
                    className="btn-secondary !py-1 !px-2.5 !text-[11px] inline-flex items-center gap-1" title="Download all slide images + captions as a ZIP">
                    <Download className="w-2.5 h-2.5" /> {busy === "zip" ? "Zipping…" : "Download ZIP"}
                  </button>
                ) : null}
              </div>
            </div>
            {p.slides?.length ? (
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid={`social-slides-${p.id}`}>
                {p.slides.map((s, i) => (
                  <div key={i} className="shrink-0 w-40 bg-[#F9F6F0] border border-[#D1C7B8] rounded-xl p-2 space-y-2" data-testid={`social-slide-${p.id}-${i}`}>
                    <div className="aspect-square rounded-lg overflow-hidden bg-[#F1ECE4] flex items-center justify-center relative">
                      {s.image ? <img src={imgSrc(s.image)} alt={`slide ${i + 1}`} className="w-full h-full object-cover" />
                        : <span className="text-[10px] text-[#9A8F7E]">No image</span>}
                      <span className="absolute top-1 left-1 text-[9px] bg-[#2D322B] text-[#F9F6F0] rounded px-1.5 py-0.5">{i + 1}</span>
                      {busy === `slide:${i}` && (
                        <div className="absolute inset-0 bg-[#2D322B]/40 flex items-center justify-center" data-testid={`social-slide-regenerating-${p.id}-${i}`}>
                          <RefreshCw className="w-5 h-5 text-[#F9F6F0] animate-spin" />
                        </div>
                      )}
                    </div>
                    <textarea rows={4} value={s.text || ""} onChange={(e) => setSlideText(i, e.target.value)} data-testid={`social-slide-text-${p.id}-${i}`}
                      className="w-full bg-transparent border border-[#D1C7B8] rounded-lg p-2 text-[11px] text-[#2D322B] outline-none focus:border-[#C05C42]" />
                    <div className="flex items-center justify-between">
                      <button type="button" onClick={() => regenSlide(i)} disabled={!!busy} title="Regenerate this slide's image" className="text-[#5C6359] hover:text-[#C05C42]" data-testid={`social-slide-regen-${p.id}-${i}`}><RefreshCw className="w-3 h-3" /></button>
                      <button type="button" onClick={() => copySlide(s.text)} className="text-[#5C6359] hover:text-[#C05C42]" data-testid={`social-slide-copy-${p.id}-${i}`}><Copy className="w-3 h-3" /></button>
                      <button type="button" onClick={() => removeSlide(i)} className="text-[#8F3A3A] hover:text-[#C05C42]" data-testid={`social-slide-remove-${p.id}-${i}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-[#9A8F7E]" data-testid={`social-carousel-empty-${p.id}`}>
                No carousel yet — pick a slide count and build one. Images come out clean & on-brand; overlay the slide copy in Canva or Instagram. Remember to <strong>Save</strong> to keep it.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: `${aud.color}22`, color: aud.color }}>{aud.label}</span>
            <div className="flex-1" />
            <button onClick={copyPost} data-testid={`social-copy-${p.id}`} className="btn-secondary !py-2 !px-3 !text-xs inline-flex items-center gap-1.5"><Copy className="w-3 h-3" /> Copy</button>
            <button onClick={remove} data-testid={`social-delete-${p.id}`} className="text-[#8F3A3A] hover:text-[#C05C42] px-2"><Trash2 className="w-4 h-4" /></button>
            <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5 inline-flex items-center gap-2" data-testid={`social-save-${p.id}`}>
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const mondayOf = (d) => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function PostChip({ post }) {
  const aud = AUDIENCES.find((a) => a.id === post.audience) || AUDIENCES[0];
  const Plat = (PLATFORMS.find((p) => p.id === post.platform) || PLATFORMS[0]).Icon;
  const snippet = (post.caption || "").split("\n")[0].slice(0, 70) || "(no caption yet)";
  const copy = () => {
    const text = [post.caption, post.hashtags].filter(Boolean).join("\n\n");
    navigator.clipboard.writeText(text).then(() => toast.success("Copied."));
  };
  return (
    <div className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-lg p-2 space-y-1" data-testid={`cal-post-${post.id}`} style={{ borderLeft: `3px solid ${aud.color}` }}>
      {post.image ? (
        <img src={imgSrc(post.image)} alt="" className="w-full h-16 object-cover rounded" data-testid={`cal-thumb-${post.id}`} />
      ) : post.images_status === "generating" ? (
        <div className="w-full h-16 rounded bg-[#F1ECE4] flex items-center justify-center text-[9px] uppercase tracking-widest text-[#9A8F7E] animate-pulse" data-testid={`cal-thumb-loading-${post.id}`}>Painting…</div>
      ) : null}
      <div className="flex items-center gap-1.5">
        <Plat className="w-3 h-3 text-[#5C6359]" />
        <span className="text-[9px] uppercase tracking-widest text-[#9A8F7E] capitalize">{post.status}</span>
        {post.slides?.length ? <span className="text-[9px] text-[#3F6B4C] inline-flex items-center gap-0.5" title={`${post.slides.length}-slide carousel`}><Images className="w-2.5 h-2.5" />{post.slides.length}</span> : null}
        <button onClick={copy} className="ml-auto text-[#5C6359] hover:text-[#C05C42]" data-testid={`cal-copy-${post.id}`}><Copy className="w-3 h-3" /></button>
      </div>
      <p className="text-[11px] text-[#2D322B] leading-snug">{snippet}</p>
    </div>
  );
}

function WeekCalendar({ posts, weekStart, onPrev, onNext, onToday }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const todayYmd = ymd(new Date());
  const unscheduled = posts.filter((p) => !p.target_date);
  const rangeLabel = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  return (
    <div data-testid="social-calendar">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onPrev} className="btn-secondary !py-1.5 !px-2" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={onToday} className="btn-secondary !py-1.5 !px-3 !text-xs" data-testid="cal-today">This week</button>
        <button onClick={onNext} className="btn-secondary !py-1.5 !px-2" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        <span className="text-sm text-[#5C6359]" data-testid="cal-range">{rangeLabel}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {days.map((d, i) => {
          const key = ymd(d);
          const dayPosts = posts.filter((p) => p.target_date === key);
          const isToday = key === todayYmd;
          return (
            <div key={key} className={`rounded-xl border p-2 min-h-[120px] ${isToday ? "border-[#C05C42] bg-[#F1ECE4]" : "border-[#D1C7B8] bg-[#F1ECE4]/60"}`} data-testid={`cal-day-${key}`}>
              <p className="text-[10px] uppercase tracking-widest text-[#5C6359] mb-2">{DAY_LABELS[i]} <span className="text-[#9A8F7E]">{d.getDate()}</span></p>
              <div className="space-y-2">
                {dayPosts.map((p) => <PostChip key={p.id} post={p} />)}
              </div>
            </div>
          );
        })}
      </div>
      {unscheduled.length > 0 && (
        <div className="mt-5" data-testid="cal-unscheduled">
          <p className="label-eyebrow mb-2">Unscheduled ({unscheduled.length})</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {unscheduled.map((p) => <PostChip key={p.id} post={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSocial() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list");
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [planning, setPlanning] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchingWomen, setLaunchingWomen] = useState(false);

  const load = () => api.get("/admin/social/posts").then((r) => setPosts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { if (user?.is_admin) load(); }, [user]); // eslint-disable-line

  // Poll until background-generated images land for the given created posts (up to 2 min).
  const pollForImages = (created) => {
    const ids = new Set((created || []).map((p) => p.id));
    const startedAt = Date.now();
    const poll = async () => {
      if (Date.now() - startedAt > 150000) return;
      try {
        const rr = await api.get("/admin/social/posts");
        const all = rr.data || [];
        setPosts(all);
        if (all.some((p) => ids.has(p.id) && p.images_status === "generating")) {
          setTimeout(poll, 5000);
        } else {
          toast.success("Images are ready.");
        }
      } catch { /* stop polling silently on error */ }
    };
    setTimeout(poll, 6000);
  };

  const addPost = async () => {
    try {
      const r = await api.post("/admin/social/posts", { platform: "instagram", audience: "members", status: "idea" });
      setPosts((arr) => [r.data, ...arr]);
      toast.success("New post added.");
    } catch { toast.error("Could not add post"); }
  };

  const planWeek = async () => {
    setPlanning(true);
    try {
      const r = await api.post("/admin/social/plan-week", { start_date: ymd(weekStart) });
      const created = r.data || [];
      setPosts((arr) => [...created, ...arr]);
      setView("calendar");
      toast.success(`Massey drafted ${created.length} posts — painting on-brand images now…`);
      pollForImages(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not plan the week");
    } finally { setPlanning(false); }
  };

  const launchWeek = async () => {
    if (!window.confirm("Build a full Feature Launch Week? This creates one carousel per major Badi feature (captions + images) in the background — takes a couple of minutes.")) return;
    setLaunching(true);
    try {
      const r = await api.post("/admin/social/feature-launch-week");
      const created = r.data || [];
      setPosts((arr) => [...created, ...arr]);
      setView("calendar");
      toast.success(`Building ${created.length} feature carousels — captions & images generating…`);
      pollForImages(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the launch week");
    } finally { setLaunching(false); }
  };

  const womensWeek = async () => {
    if (!window.confirm("Build the Women's Vitality launch set? This creates one carousel per theme across the three life stages (captions + images) in the background — takes a couple of minutes.")) return;
    setLaunchingWomen(true);
    try {
      const r = await api.post("/admin/social/womens-launch-week");
      const created = r.data || [];
      setPosts((arr) => [...created, ...arr]);
      setView("calendar");
      toast.success(`Building ${created.length} Women's Vitality carousels — captions & images generating…`);
      pollForImages(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the Women's Vitality set");
    } finally { setLaunchingWomen(false); }
  };

  const onSaved = (u) => setPosts((arr) => arr.map((x) => (x.id === u.id ? u : x)));
  const onDeleted = (id) => { setPosts((arr) => arr.filter((x) => x.id !== id)); toast.success("Post removed."); };

  const shown = filter === "all" ? posts : posts.filter((p) => p.audience === filter || p.status === filter || p.platform === filter);

  return (
    <AdminLayout>
      <div data-testid="admin-social-page">
        <p className="label-eyebrow mb-2">Admin · Social</p>
        <h1 className="font-serif text-4xl text-[#2D322B] leading-tight">Plan the feed.</h1>
        <p className="mt-4 text-[#5C6359] max-w-2xl text-sm">
          Your team's internal planning hub for Instagram &amp; Threads. Draft posts with Massey, generate or upload images,
          tag each for Members or Clinicians, then copy-paste to publish. Nothing is posted automatically.
        </p>

        <div className="mt-8 space-y-8">
          <StrategyEditor />

          <section>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
              <div className="flex flex-wrap items-center gap-2">
                {/* view toggle */}
                <div className="inline-flex rounded-lg border border-[#D1C7B8] overflow-hidden" data-testid="social-view-toggle">
                  <button onClick={() => setView("list")} data-testid="social-view-list"
                    className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${view === "list" ? "bg-[#2D322B] text-[#F9F6F0]" : "text-[#5C6359]"}`}>
                    <ListIcon className="w-3 h-3" /> List
                  </button>
                  <button onClick={() => setView("calendar")} data-testid="social-view-calendar"
                    className={`px-3 py-1.5 text-xs inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-[#2D322B] text-[#F9F6F0]" : "text-[#5C6359]"}`}>
                    <CalendarDays className="w-3 h-3" /> Week
                  </button>
                </div>
                {view === "list" && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="social-filters">
                    {["all", "members", "clinicians", "instagram", "threads", "idea", "scheduled", "posted"].map((f) => (
                      <button key={f} onClick={() => setFilter(f)} data-testid={`social-filter-${f}`}
                        className={`text-[11px] uppercase tracking-widest rounded-full px-3 py-1 border transition-colors capitalize ${filter === f ? "bg-[#2D322B] text-[#F9F6F0] border-[#2D322B]" : "text-[#5C6359] border-[#D1C7B8] hover:border-[#C05C42]"}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button onClick={womensWeek} disabled={launchingWomen || launching || planning} className="btn-secondary !py-2 !px-4 inline-flex items-center gap-2" data-testid="social-womens-week" title="Build the Women's Vitality launch set (Cycles & Seasons)">
                  <Sparkles className="w-4 h-4" /> {launchingWomen ? "Building women's…" : "Women's Vitality Week"}
                </button>
                <button onClick={launchWeek} disabled={launching || planning} className="btn-secondary !py-2 !px-4 inline-flex items-center gap-2" data-testid="social-launch-week" title="Build one carousel per major Badi feature">
                  <Sparkles className="w-4 h-4" /> {launching ? "Building features…" : "Feature Launch Week"}
                </button>
                <button onClick={planWeek} disabled={planning || launching} className="btn-secondary !py-2 !px-4 inline-flex items-center gap-2" data-testid="social-plan-week">
                  <Wand2 className="w-4 h-4" /> {planning ? "Drafting a week…" : "Draft a week's worth"}
                </button>
                <button onClick={addPost} className="btn-primary !py-2 !px-5 inline-flex items-center gap-2" data-testid="social-add-post">
                  <Plus className="w-4 h-4" /> New post
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-[#5C6359]" data-testid="social-loading">Loading…</p>
            ) : view === "calendar" ? (
              <WeekCalendar
                posts={posts}
                weekStart={weekStart}
                onPrev={() => setWeekStart((w) => addDays(w, -7))}
                onNext={() => setWeekStart((w) => addDays(w, 7))}
                onToday={() => setWeekStart(mondayOf(new Date()))}
              />
            ) : shown.length === 0 ? (
              <p className="text-sm text-[#5C6359] bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6" data-testid="social-empty">No posts yet. Add your first above.</p>
            ) : (
              <div className="space-y-4">
                {shown.map((p) => <PostEditor key={p.id} post={p} onSaved={onSaved} onDeleted={onDeleted} />)}
              </div>
            )}
          </section>
        </div>
      </div>
    </AdminLayout>
  );
}
